import { useState, useEffect } from 'react'
import { Euro, RefreshCw, Trash2, Copy, AlertTriangle, CheckCircle, FileSpreadsheet, Download, X, Building2, CheckSquare, FileText, Search as SearchIcon } from 'lucide-react'
import { PieChart as PieChartIcon, History, TrendingUp } from 'lucide-react'
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import toast from 'react-hot-toast'
import api from '../../services/api'

export default function AccountingPage() {
    //const [activeTab, setActiveTab] = useState('stancer') // 'stancer' ou 'tpp'
    const [activeTab, setActiveTab] = useState('overview') // 'overview', 'stancer', 'tpp', 'history'
    const [revenueStats, setRevenueStats] = useState(null)
    const [tppHistory, setTppHistory] = useState([])
    const COLORS =['#7d9668', '#f5b78a', '#9333ea', '#3b82f6', '#eab308', '#ef4444', '#14b8a6', '#f43f5e'];
    const [pendingLinks, setPendingLinks] = useState([])
    const [pendingTpp, setPendingTpp] = useState([]) // Les impayés Tiers-Payant
    const [loading, setLoading] = useState(true)
    const[syncing, setSyncing] = useState(false)
    
    // NOUVEAU : État des cases à cocher pour le Tiers-Payant
    const [selectedTrans, setSelectedTrans] = useState(new Set())
    
    // États pour l'export CSV
    const[isExportModalOpen, setIsExportModalOpen] = useState(false)
    const [exportDates, setExportDates] = useState({ start: '', end: '' })

    const fetchData = async () => {
        setLoading(true)
        try {
            const [linksRes, tppRes, statsRes, histRes] = await Promise.all([
                api.get('/api/payments/pending-stancer'),
                api.get('/api/payments/third-party/pending'),
                api.get('/api/payments/stats/revenue'),
                api.get('/api/payments/third-party/history')
            ])
            setPendingLinks(linksRes.data)
            setPendingTpp(tppRes.data)
            setRevenueStats(statsRes.data)
            setTppHistory(histRes.data)
            
            // Par défaut, on coche toutes les transactions Tiers-Payant
            const allIds = new Set()
            tppRes.data.forEach(group => group.transactions.forEach(t => allIds.add(t.id)))
            setSelectedTrans(allIds)
            
        } catch (error) {

            toast.error("Erreur de chargement des données")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    },[])


    const handleSyncStancer = async () => {
        setSyncing(true)
        const toastId = toast.loading("Synchronisation avec Stancer en cours...")
        try {
            const res = await api.post('/api/payments/sync-stancer')
            toast.success(res.data.message, { id: toastId })
            fetchData()
        } catch (error) {
            toast.error("Erreur lors de la synchronisation", { id: toastId })
        } finally {
            setSyncing(false)
        }
    }

    const handleDeleteTransaction = async (id) => {
        if (!confirm("Annuler définitivement ce lien de paiement ? (Il ne sera plus utilisable par le patient)")) return
        
        try {
            await api.delete(`/api/payments/transaction/${id}`)
            toast.success("Lien annulé et supprimé")
            fetchData()
        } catch (error) {
            toast.error("Erreur lors de l'annulation")
        }
    }

    const handleCopyLink = (link) => {
        navigator.clipboard.writeText(link)
        toast.success("Lien copié !")
    }

    const setQuickDate = (type) => {
        const today = new Date()
        if (type === 'current_month') {
            setExportDates({
                start: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
                end:   new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0]
            })
        } else if (type === 'last_month') {
            setExportDates({
                start: new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0],
                end:   new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0]
            })
        } else if (type === 'year') {
            setExportDates({
                start: new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0],
                end:   new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0]
            })
        }
    }

    const handlePreviewSpecificBordereau = (tppId, tppName, transaction) => {
        // En envoyant juste cette transaction, le backend détectera qu'elle appartient à un bordereau et le renverra direct !
        handleGenerateTppInvoice(tppId, tppName, [transaction]);
    }

    const handleExportCSV = async () => {
        if (!exportDates.start || !exportDates.end) return toast.error("Veuillez sélectionner une période")
        try {
            toast.loading('Génération du fichier...', { id: 'exportCsv' })
            const response = await api.get('/api/invoices/export/csv', {
                params: { start_date: exportDates.start, end_date: exportDates.end },
                responseType: 'blob'
            })
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `Compta_${exportDates.start}_au_${exportDates.end}.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            toast.success('Export réussi !', { id: 'exportCsv' })
            setIsExportModalOpen(false)
        } catch (e) {
            toast.error("Erreur lors de l'export", { id: 'exportCsv' })
        }
    }

    // --- TELECHARGEMENT HISTORIQUE TPP ---
    const handleDownloadTppHistory = async (invoiceNumber) => {
        try {
            toast.loading("Téléchargement en cours...", { id: 'dl_tpp' })
            const response = await api.get(`/api/payments/third-party/history/${invoiceNumber}/download`, { responseType: 'blob' })
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `Bordereau_${invoiceNumber}.pdf`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            toast.success("Bordereau téléchargé !", { id: 'dl_tpp' })
        } catch (e) {
            toast.error("Fichier introuvable sur le serveur.", { id: 'dl_tpp' })
        }
    }

    // --- GESTION TIERS PAYANT ---
    const handleGenerateTppInvoice = async (tppId, tppName, transactions) => {
        try {
            toast.loading("Génération du bordereau...", { id: 'tpp_inv' })
            const newWindow = window.open('', '_blank');
            const transactionIds = transactions.map(t => t.id)
            
            const response = await api.post(`/api/payments/third-party/${tppId}/generate-invoice`, 
                { transaction_ids: transactionIds }, 
                { responseType: 'blob' }
            )
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            
            newWindow.document.write(`
                <html><head><title>Bordereau - ${tppName}</title><style>body { margin: 0; overflow: hidden; }</style></head>
                <body><iframe src="${url}" width="100%" height="100%" frameborder="0"></iframe></body></html>
            `);
            newWindow.document.close();
            
            toast.success("Bordereau ouvert !", { id: 'tpp_inv' })
            
            // On rafraîchit la page pour faire apparaître le badge avec le numéro de facture !
            fetchData() 
        } catch (error) {
            toast.error("Erreur de génération", { id: 'tpp_inv' })
        }
    }


    const handleReconcileTpp = async (tppName, transactions) => {
        const total = transactions.reduce((sum, t) => sum + t.amount, 0);
        if (!confirm(`Confirmez-vous avoir reçu le virement de ${total.toFixed(2)}€ de la part de ${tppName} ?\nLes ${transactions.length} RDV seront marqués comme pointés.`)) return;
        
        try {
            const transactionIds = transactions.map(t => t.id)
            const res = await api.post(`/api/payments/third-party/reconcile`, { transaction_ids: transactionIds })
            toast.success(res.data.message)
            fetchData() // Rafraîchit les listes
        } catch (error) {
            toast.error("Erreur lors du pointage")
        }
    }

    // --- GESTION DES CASES A COCHER ---
    const toggleTransaction = (id) => {
        const newSet = new Set(selectedTrans)
        if (newSet.has(id)) newSet.delete(id)
        else newSet.add(id)
        setSelectedTrans(newSet)
    }

    const toggleAllGroup = (transactions) => {
        const newSet = new Set(selectedTrans)
        const allSelected = transactions.every(t => newSet.has(t.id))
        
        transactions.forEach(t => {
            if (allSelected) newSet.delete(t.id)
            else newSet.add(t.id)
        })
        setSelectedTrans(newSet)
    }


    if (loading) return <div className="flex justify-center py-20"><div className="spinner" /></div>

    return (

        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-serif text-gray-800">Comptabilité & Suivi</h1>
                    <p className="text-gray-500 mt-1">Supervisez vos encaissements et liens de paiement.</p>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => { setQuickDate('current_month'); setIsExportModalOpen(true) }}
                        className="btn bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 shadow-sm transition-all"
                    >
                        <FileSpreadsheet className="w-4 h-4 text-sage-600" /> Export CSV
                    </button>
                    <button 
                        onClick={handleSyncStancer}
                        disabled={syncing || pendingLinks.length === 0}
                        className="btn bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 shadow-md transition-all disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                        Synchroniser Stancer
                    </button>
                </div>
            </div>

            {/* ONGLETS */}
            <div className="flex gap-4 border-b border-gray-200 overflow-x-auto scrollbar-hide">
                <button 
                    onClick={() => setActiveTab('overview')}
                    className={`pb-3 px-2 flex items-center gap-2 font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === 'overview' ? 'border-sage-500 text-sage-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                    <PieChartIcon className="w-4 h-4" /> Cockpit Financier
                </button>
                <button 
                    onClick={() => setActiveTab('stancer')}
                    className={`pb-3 px-2 flex items-center gap-2 font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === 'stancer' ? 'border-sage-500 text-sage-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                    <Euro className="w-4 h-4" /> Liens CB Patients
                    {pendingLinks.length > 0 && <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{pendingLinks.length}</span>}
                </button>
                <button 
                    onClick={() => setActiveTab('tpp')}
                    className={`pb-3 px-2 flex items-center gap-2 font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === 'tpp' ? 'border-sage-500 text-sage-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                    <Building2 className="w-4 h-4" /> Facturation Tiers-Payant
                    {pendingTpp.length > 0 && <span className="bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{pendingTpp.length}</span>}
                </button>
                <button 
                    onClick={() => setActiveTab('history')}
                    className={`pb-3 px-2 flex items-center gap-2 font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === 'history' ? 'border-sage-500 text-sage-700' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                    <History className="w-4 h-4" /> Historique Bordereaux
                </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                {/* VUE COCKPIT FINANCIER */}
                {activeTab === 'overview' && revenueStats && (
                    <div className="p-6 space-y-8 animate-in fade-in">
                        {/* Courbe globale CA */}
                        <div className="border border-gray-100 rounded-xl p-6 shadow-sm bg-white">
                            <h3 className="text-sm font-bold text-gray-700 uppercase mb-4 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-sage-600" /> Évolution du Chiffre d'Affaires (12 mois)
                            </h3>
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={revenueStats.monthly}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                        <XAxis dataKey="month" tickFormatter={(val) => { const[y, m] = val.split('-'); return `${m}/${y.slice(2)}` }} stroke="#9ca3af" fontSize={12} />
                                        <YAxis stroke="#9ca3af" fontSize={12} unit="€" />
                                        <Tooltip formatter={(value) =>[`${value} €`, "Chiffre d'Affaires"]} labelFormatter={(label) => `Mois : ${label}`} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                        <Line type="monotone" dataKey="total" stroke="#7d9668" strokeWidth={4} dot={{ fill: '#7d9668', strokeWidth: 2, r: 4 }} activeDot={{ r: 8 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Répartitions : Méthodes & Genre & TPP */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="border border-gray-100 rounded-xl p-6 shadow-sm bg-white">
                                <h3 className="text-sm font-bold text-gray-700 uppercase mb-4 text-center">Encaissements par méthode</h3>
                                <div className="h-[250px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={revenueStats.by_method} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                                {revenueStats.by_method.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip formatter={(value) => [`${value} €`, "Montant"]} />
                                            <Legend verticalAlign="bottom" height={36} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            
                            <div className="border border-gray-100 rounded-xl p-6 shadow-sm bg-white">
                                <h3 className="text-sm font-bold text-gray-700 uppercase mb-4 text-center">Part du CA (Homme / Femme)</h3>
                                <div className="h-[250px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={revenueStats.by_gender} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                                {revenueStats.by_gender.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.name === 'Femme' ? '#f472b6' : entry.name === 'Homme' ? '#60a5fa' : '#94a3b8'} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value) => [`${value} €`, "Montant"]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* NOUVEAU CAMEMBERT : TIERS PAYANT */}
                            <div className="border border-gray-100 rounded-xl p-6 shadow-sm bg-white">
                                <h3 className="text-sm font-bold text-gray-700 uppercase mb-4 text-center">Tunnel Tiers-Payant</h3>
                                <div className="h-[250px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        {revenueStats.by_tpp_status && revenueStats.by_tpp_status.length > 0 ? (
                                            <PieChart>
                                                <Pie data={revenueStats.by_tpp_status} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                                                    {revenueStats.by_tpp_status.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={
                                                            entry.name === 'Pointés (Payés)' ? '#4ade80' : 
                                                            entry.name === 'Attente Virement' ? '#fb923c' : 
                                                            '#60a5fa' // À facturer
                                                        } />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(value) =>[`${value} €`, "Montant"]} />
                                                <Legend verticalAlign="bottom" height={36} />
                                            </PieChart>
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-xs text-gray-400 italic">
                                                Aucun acte en Tiers-Payant.
                                            </div>
                                        )}
                                    </ResponsiveContainer>
                                </div>
                            </div> 
                        </div>

                        {/* BarChart Motifs */}
                        <div className="border border-gray-100 rounded-xl p-6 shadow-sm bg-white">
                            <h3 className="text-sm font-bold text-gray-700 uppercase mb-4 flex items-center gap-2">
                                <PieChartIcon className="w-4 h-4 text-blue-500" /> Motifs de consultation les plus rentables
                            </h3>
                            <div className="h-[300px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={revenueStats.by_reason} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee" />
                                        <XAxis type="number" unit="€" fontSize={12} stroke="#9ca3af" />
                                        <YAxis dataKey="name" type="category" width={150} fontSize={10} stroke="#4b5563" />
                                        <Tooltip formatter={(value) => [`${value} €`, "CA Généré"]} cursor={{fill: '#f3f4f6'}} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}/>
                                        <Bar dataKey="value" fill="#60a5fa" radius={[0, 4, 4, 0]}>
                                            {revenueStats.by_reason.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                )}

                {/* VUE HISTORIQUE TIERS-PAYANT */}
                {activeTab === 'history' && (
                    <div className="animate-in fade-in">
                        <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                            <History className="w-5 h-5 text-gray-500" />
                            <h2 className="font-bold text-gray-700">Historique des Bordereaux (Tiers-Payant)</h2>
                        </div>
                        {tppHistory.length === 0 ? (
                            <div className="p-12 text-center text-gray-400">Aucun bordereau n'a encore été généré.</div>
                        ) : (
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-[10px]">
                                    <tr>
                                        <th className="px-6 py-4">Date de génération</th>
                                        <th className="px-6 py-4">Numéro</th>
                                        <th className="px-6 py-4">Partenaire</th>
                                        <th className="px-6 py-4 text-right">Montant Facturé</th>
                                        <th className="px-6 py-4 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {tppHistory.map((inv, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 text-gray-500">{format(new Date(inv.date), 'dd/MM/yyyy à HH:mm')}</td>
                                            <td className="px-6 py-4 font-mono font-bold text-sage-600">{inv.invoice_number}</td>
                                            <td className="px-6 py-4 font-medium text-gray-800">{inv.tpp_name}</td>
                                            <td className="px-6 py-4 text-right font-bold text-blue-600">{inv.total_amount.toFixed(2)} €</td>
                                            <td className="px-6 py-4 text-center">
                                                {inv.path ? (
                                                    <button 
                                                        onClick={() => handleDownloadTppHistory(inv.invoice_number)}
                                                        className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-colors inline-block" 
                                                        title="Télécharger le bordereau"
                                                    >
                                                        <Download className="w-4 h-4 mx-auto" />
                                                    </button>
                                                ) : <span className="text-xs text-gray-400">Introuvable</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
                {activeTab === 'stancer' && (
                    <>
                        <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-500" />
                            <h2 className="font-bold text-gray-700">Liens CB en attente ({pendingLinks.length})</h2>
                        </div>

                {pendingLinks.length === 0 ? (
                    <div className="p-12 text-center text-gray-400 flex flex-col items-center">
                        <CheckCircle className="w-12 h-12 text-green-300 mb-3" />
                        <p>Tout est à jour ! Aucun paiement en attente.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                                <tr>
                                    <th className="px-6 py-4">Créé le</th>
                                    <th className="px-6 py-4">Patient</th>
                                    <th className="px-6 py-4">Montant</th>
                                    <th className="px-6 py-4">Statut actuel</th>
                                    <th className="px-6 py-4">Lien de paiement</th>
                                    <th className="px-6 py-4 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pendingLinks.map(link => (
                                    <tr key={link.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 text-gray-500">
                                            {format(new Date(link.created_at), 'dd/MM/yyyy à HH:mm')}
                                        </td>
                                        <td className="px-6 py-4 font-bold text-gray-800">
                                            {link.patient_name}
                                            <div className="text-xs font-normal text-gray-400 mt-0.5">{link.patient_phone}</div>
                                        </td>
                                        <td className="px-6 py-4 font-bold text-blue-600">
                                            {link.amount} €
                                        </td>
                                        <td className="px-6 py-4">
                                            {link.status === 'forced' ? (
                                                <span className="bg-orange-100 text-orange-700 border border-orange-200 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider">
                                                    Forcé (Facturé)
                                                </span>
                                            ) : (
                                                <span className="bg-yellow-100 text-yellow-700 border border-yellow-300 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider">
                                                    En attente patient
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    type="text" 
                                                    readOnly 
                                                    value={link.payment_link} 
                                                    className="w-32 text-xs bg-gray-100 border-none rounded py-1 px-2 text-gray-500 font-mono"
                                                />
                                                <button onClick={() => handleCopyLink(link.payment_link)} className="text-gray-400 hover:text-blue-600" title="Copier le lien">
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button 
                                                onClick={() => handleDeleteTransaction(link.id)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 bg-white border border-transparent hover:border-red-200 hover:bg-red-50 rounded transition-all"
                                                title="Annuler ce lien"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                </>
            )}

            {/* VUE TIERS PAYANT */}
            {activeTab === 'tpp' && (
                <>
                    <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-blue-500" />
                        <h2 className="font-bold text-gray-700">À facturer aux Partenaires / Associations</h2>
                    </div>
                    
                    {pendingTpp.length === 0 ? (
                        <div className="p-12 text-center text-gray-400 flex flex-col items-center">
                            <CheckCircle className="w-12 h-12 text-green-300 mb-3" />
                            <p>Aucune facture en attente pour vos partenaires.</p>
                        </div>
                    ) : (
                        <div className="p-4 space-y-6">
                            {pendingTpp.map(group => {
                                // On isole les transactions sélectionnées pour CE groupe
                                const groupSelectedTrans = group.transactions.filter(t => selectedTrans.has(t.id));
                                const groupSelectedTotal = groupSelectedTrans.reduce((sum, t) => sum + t.amount, 0);
                                const allGroupSelected = group.transactions.length > 0 && group.transactions.every(t => selectedTrans.has(t.id));

                                // Logique UX pour le bouton Bordereau
                                const unbilledSelected = groupSelectedTrans.filter(t => !t.tpp_invoice_number);
                                const uniqueInvoices = new Set(groupSelectedTrans.filter(t => t.tpp_invoice_number).map(t => t.tpp_invoice_number));
                                
                                let btnText = "Éditer Bordereau";
                                let btnDisabled = groupSelectedTrans.length === 0;
                                let btnTitle = "";

                                if (groupSelectedTrans.length > 0) {
                                    if (unbilledSelected.length === 0) {
                                        if (uniqueInvoices.size === 1) {
                                            btnText = `Voir le Bordereau`;
                                        } else {
                                            btnDisabled = true;
                                            btnText = "Bordereaux multiples";
                                            btnTitle = "Impossible de fusionner des anciens bordereaux. Utilisez la petite loupe sur la ligne pour les voir un par un.";
                                        }
                                    } else {
                                        btnText = `Créer Bordereau (${unbilledSelected.length})`;
                                    }
                                }

                                return (
                                <div key={group.tpp_id} className="border border-blue-100 rounded-xl overflow-hidden shadow-sm">
                                    {/* Header du bloc Asso */}
                                    <div className="bg-blue-50 p-4 flex justify-between items-center border-b border-blue-100">
                                        <div>
                                            <h3 className="font-bold text-blue-900 text-lg">{group.tpp_name}</h3>
                                            <p className="text-sm text-blue-700">{group.transactions.length} consultation(s) en attente</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-blue-700 uppercase font-bold tracking-wider">Sélectionné pour facturation</p>
                                            <p className="text-2xl font-black text-blue-600">{groupSelectedTotal.toFixed(2)} €</p>
                                        </div>
                                    </div>
                                    
                                    {/* Liste des patients avec cases à cocher */}
                                    <div className="bg-white">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-gray-50/50 text-gray-400 font-bold uppercase text-[10px]">
                                                <tr>
                                                    <th className="px-4 py-2 w-10 text-center">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={allGroupSelected}
                                                            onChange={() => toggleAllGroup(group.transactions)}
                                                            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                                                        />
                                                    </th>
                                                    <th className="px-2 py-2">Date du RDV</th>
                                                    <th className="px-6 py-2">Patient</th>
                                                    <th className="px-6 py-2 text-right">Montant</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {group.transactions.map(t => (
                                                    <tr key={t.id} className="hover:bg-blue-50/30 transition-colors">
                                                        <td className="px-4 py-3 text-center">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={selectedTrans.has(t.id)}
                                                                onChange={() => toggleTransaction(t.id)}
                                                                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                                                            />
                                                        </td>
                                                        <td className="px-2 py-3 text-gray-500">{format(new Date(t.date), 'dd/MM/yyyy')}</td>
                                                        <td className="px-6 py-3 font-medium text-gray-700">
                                                            {t.patient_name}
                                                            {t.tpp_invoice_number && (
                                                                <span className="ml-2 inline-flex items-center gap-1 bg-sage-100 text-sage-700 text-[10px] pl-2 pr-1 py-0.5 rounded-full font-bold border border-sage-200">
                                                                    {t.tpp_invoice_number}
                                                                    <button 
                                                                        onClick={() => handlePreviewSpecificBordereau(group.tpp_id, group.tpp_name, t)}
                                                                        className="hover:text-blue-600 hover:bg-white p-0.5 rounded"
                                                                        title="Voir le bordereau"
                                                                    >
                                                                        <SearchIcon className="w-3 h-3" />
                                                                    </button>
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-3 text-right font-bold text-gray-800">{t.amount.toFixed(2)} €</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Actions */}
                                    <div className="bg-gray-50 p-3 flex justify-end gap-3 border-t border-gray-100">
                                        <button 
                                            disabled={btnDisabled}
                                            title={btnTitle}
                                            onClick={() => handleGenerateTppInvoice(group.tpp_id, group.tpp_name, groupSelectedTrans)}
                                            className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center gap-2 text-sm disabled:opacity-50"
                                        >
                                            <FileText className="w-4 h-4" /> 
                                            {btnText}
                                        </button>
                                        <button 
                                            disabled={groupSelectedTrans.length === 0}
                                            onClick={() => handleReconcileTpp(group.tpp_name, groupSelectedTrans)}
                                            className="btn bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 text-sm disabled:opacity-50 shadow-md"
                                        >
                                            <CheckSquare className="w-4 h-4" /> 
                                            Valider le Virement ({groupSelectedTotal.toFixed(2)}€)
                                        </button>
                                    </div>
                                </div>
                            )})}
                        </div>
                    )}
                </>
            )}
            </div>

            {/* MODALE EXPORT CSV */}
            {isExportModalOpen && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 animate-in zoom-in-95">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-serif font-bold text-gray-800 flex items-center gap-2">
                                <FileSpreadsheet className="w-5 h-5 text-sage-600" /> Export Comptable
                            </h3>
                            <button onClick={() => setIsExportModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                <button onClick={() => setQuickDate('last_month')}    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg whitespace-nowrap font-medium transition-colors">Mois précédent</button>
                                <button onClick={() => setQuickDate('current_month')} className="text-xs bg-sage-100 hover:bg-sage-200 text-sage-700 px-3 py-1.5 rounded-lg whitespace-nowrap font-medium transition-colors">Ce mois-ci</button>
                                <button onClick={() => setQuickDate('year')}          className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg whitespace-nowrap font-medium transition-colors">Cette année</button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Du</label>
                                    <input type="date" value={exportDates.start} onChange={e => setExportDates({...exportDates, start: e.target.value})} className="w-full text-sm py-2" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Au</label>
                                    <input type="date" value={exportDates.end} onChange={e => setExportDates({...exportDates, end: e.target.value})} className="w-full text-sm py-2" />
                                </div>
                            </div>
                            <button onClick={handleExportCSV} className="w-full btn btn-primary flex justify-center items-center gap-2 mt-4">
                                <Download className="w-4 h-4" /> Télécharger le CSV
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}